import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "../lib/api";
import { toast } from "sonner";
import { playSaveChime } from "../lib/sound";
import { runPdfExportJob, downloadBlobAsFile } from "../lib/pdfExportJob";
import AgentInputScreen from "./AgentInputScreen";
import AgentOutputScreen from "./AgentOutputScreen";
import AgentTabHeader from "./AgentTabHeader";
import SaveToVaultModal from "./SaveToVaultModal";

/**
 * Foundation agent shell - orchestrates between Input and Output screens
 * with a unified tab header at the top.
 *
 * New props:
 *   agentName        Header label, e.g. "Márkahang Varázsló™" (defaults to title)
 *   deliverableLabel Tab 2 label, e.g. "Márkahang", "Avatar", "Ajánlat"
 */
export default function AgentWorkflow({
  accent,
  agentKey,
  dashboardKey,
  blueprintName,
  title,
  subtitle,
  greeting,
  eyebrow,
  steps,
  agentName,
  deliverableLabel = "Output",
  readyTitle,
  fastLabel,
  fastDesc,
  guidedLabel,
  guidedDesc,
  generateEndpoint,
  loadEndpoint,
  saveEndpoint,
  refineEndpoint,
  renderBlueprint,
  pickRandomSample,
  nichePills,
  fastTrackKeys,
  serializeValues,
  deserializeValues,
  cycleDemoPill = false,
  contentInputMode = "classic",
  eyebrowVariant = "brand",
  showContentInput = true,
  fastVariant = "upload",
  requiresAvatarSource = false,
  modePrompt,
  guidedIntro,
  demoFillEndpoint,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const draftKey = `mex.draft.${agentKey}`;
  const [values, setValues] = useState({});
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState("");
  const [genError, setGenError] = useState(null);
  const [copiedAt, setCopiedAt] = useState(0);
  const isCopied = copiedAt > 0 && Date.now() - copiedAt < 2000;
  useEffect(() => {
    if (!copiedAt) return undefined;
    const t = setTimeout(() => setCopiedAt(0), 2000);
    return () => clearTimeout(t);
  }, [copiedAt]);
  const [blueprint, setBlueprint] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [savingVault, setSavingVault] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [randomNiche, setRandomNiche] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [view, setView] = useState("input"); // 'input' | 'output'
  const [contentState, setContentState] = useState({
    paste: "",
    urls: [],
    mode: null,
    writingSample: "",
  });
  const [hydrated, setHydrated] = useState(false);
  const genTokenRef = useRef(0);
  const suppPollTokenRef = useRef(0);

  // Avatar V2 supplementary polling — runs after the critical 7 sections
  // come back. Merges the 13 supplementary keys into the blueprint state
  // as they arrive in the background.
  const pollAvatarSupplementary = React.useCallback(async (suppJobId) => {
    if (!suppJobId) return;
    const myToken = ++suppPollTokenRef.current;
    const startedAt = Date.now();
    // Mark loading state on the blueprint.
    setBlueprint((prev) => prev ? { ...prev, supplementary_status: "loading" } : prev);
    try {
      let firstPoll = true;
      while (Date.now() - startedAt < 5 * 60 * 1000) {
        if (myToken !== suppPollTokenRef.current) return;
        await new Promise((r) => setTimeout(r, firstPoll ? 1500 : 2500));
        firstPoll = false;
        try {
          const poll = await api.get(`/generation/jobs/${suppJobId}`, { timeout: 15000 });
          const pdata = poll.data;
          if (pdata.status === "done") {
            const supp = pdata.result?.supplementary || {};
            setBlueprint((prev) => prev ? {
              ...prev,
              ...supp,
              supplementary_status: "done",
            } : prev);
            return;
          }
          if (pdata.status === "error") {
            throw new Error(pdata.error || "Háttér-generálás sikertelen");
          }
        } catch (pe) {
          // Transient network errors — continue polling.
          if (Date.now() - startedAt > 5 * 60 * 1000) throw pe;
        }
      }
      throw new Error("Időtúllépés a háttér-generáláson");
    } catch (e) {
      if (myToken !== suppPollTokenRef.current) return;
      setBlueprint((prev) => prev ? {
        ...prev,
        supplementary_status: "error",
        _supp_error: e?.message || "Háttér-generálás sikertelen",
      } : prev);
    }
  }, []);

  const handleSuppRetry = React.useCallback(async () => {
    if (agentKey !== "client") return;
    try {
      setBlueprint((prev) => prev ? {
        ...prev,
        supplementary_status: "loading",
        _supp_error: null,
      } : prev);
      const res = await api.post("/foundation/avatar/supplementary/retry");
      if (res.data?.supp_job_id) {
        pollAvatarSupplementary(res.data.supp_job_id);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Újraindítás sikertelen";
      toast.error(msg);
      setBlueprint((prev) => prev ? {
        ...prev,
        supplementary_status: "error",
        _supp_error: msg,
      } : prev);
    }
  }, [agentKey, pollAvatarSupplementary]);

  // CSAK a `_error`-os szekciókat regenerálja. Akkor jelenik meg a frontend
  // banner gomb, ha bármelyik supplementary szekcióban `_error` van (pl.
  // budget kifogyott a párhuzamos hívások közepén). A `pollAvatarSupplementary`
  // mintát követi (5 perces poll-ablak), és a job done-ja után GET-tel
  // újratölti a teljes Avatar-t a `loadEndpoint`-ról.
  const handleRetryFailedSections = React.useCallback(async () => {
    if (agentKey !== "client") return;
    const myToken = ++suppPollTokenRef.current;
    try {
      setBlueprint((prev) => prev ? {
        ...prev,
        supplementary_status: "loading",
        _supp_error: null,
      } : prev);
      const res = await api.post("/foundation/avatar/sections/retry-failed");
      const jobId = res.data?.job_id;
      const failedList = res.data?.failed_sections || [];
      if (!jobId) {
        toast.error("A retry nem indult el (nincs job_id).");
        return;
      }
      toast.success(`${failedList.length} hibás szekció + szintézis újragenerálása elindult…`);

      const startedAt = Date.now();
      let firstPoll = true;
      while (Date.now() - startedAt < 5 * 60 * 1000) {
        if (myToken !== suppPollTokenRef.current) return;
        await new Promise((r) => setTimeout(r, firstPoll ? 1500 : 2500));
        firstPoll = false;
        try {
          const poll = await api.get(`/generation/jobs/${jobId}`, { timeout: 15000 });
          const pdata = poll.data;
          if (pdata.status === "done") {
            // Teljes Avatar újratöltése - egyszerűbb mint csak a regenerált
            // kulcsokat mergeli, és garantáljuk, hogy a renderer a friss
            // állapotot látja.
            try {
              const reload = await api.get("/foundation/avatar");
              if (reload.data?.blueprint) {
                setBlueprint(reload.data.blueprint);
                setUpdatedAt(reload.data.updated_at || null);
              }
            } catch {}
            toast.success("Hibás szekciók újragenerálva.");
            return;
          }
          if (pdata.status === "error") {
            throw new Error(pdata.error || "Háttér-generálás sikertelen");
          }
        } catch (pe) {
          if (Date.now() - startedAt > 5 * 60 * 1000) throw pe;
        }
      }
      throw new Error("Időtúllépés a háttér-generáláson");
    } catch (e) {
      if (myToken !== suppPollTokenRef.current) return;
      const msg = e?.response?.data?.detail || e?.message || "Hibás szekciók újragenerálása sikertelen";
      toast.error(msg);
      setBlueprint((prev) => prev ? {
        ...prev,
        supplementary_status: "error",
        _supp_error: msg,
      } : prev);
    }
  }, [agentKey]);

  // Avatar V2 / Offer V2 — "Forrásanyag bővítése" flow. A user adds optional
  // clarifications, the backend re-runs the FULL generation with expansions
  // appended to the user context. Then we re-poll the new job.
  const handleExpandSource = React.useCallback(async (expansions) => {
    if (agentKey !== "client" && agentKey !== "offer") return;
    const endpoint = agentKey === "client"
      ? "/foundation/avatar/expand-source"
      : "/foundation/offer/expand-source";
    try {
      const res = await api.post(endpoint, { expansions });
      const jobId = res.data?.job_id;
      if (!jobId) {
        toast.error("A bővítés nem indult el");
        return;
      }
      toast.success(`${agentKey === "client" ? "Avatar" : "Ajánlat"} újragenerálása elindult…`);
      setBlueprint(null);
      setUpdatedAt(null);
      const startedAt = Date.now();
      while (Date.now() - startedAt < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const poll = await api.get(`/generation/jobs/${jobId}`, { timeout: 15000 });
          const pdata = poll.data;
          if (pdata.status === "done") {
            const result = pdata.result || {};
            setBlueprint(result.blueprint);
            setUpdatedAt(result.updated_at);
            if (result.supp_job_id) pollAvatarSupplementary(result.supp_job_id);
            toast.success(`Az új ${agentKey === "client" ? "Avatar" : "Ajánlat"} elkészült`);
            return;
          }
          if (pdata.status === "error") {
            throw new Error(pdata.error || "Bővítés sikertelen");
          }
        } catch (pe) {
          if (Date.now() - startedAt > 5 * 60 * 1000) throw pe;
        }
      }
      throw new Error("Időtúllépés a bővítésen");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Bővítés sikertelen";
      toast.error(msg);
    }
  }, [agentKey, pollAvatarSupplementary]);

  // Hydrate existing blueprint on mount, then layer the localStorage draft
  // on top so unsaved input changes survive navigation/refresh.
  //
  // URL flags from the Identitás page:
  //   ?new=1            — start a fresh creation: skip live-doc fetch, clear
  //                       localStorage draft. The user lands on the input
  //                       screen with the mode selector visible.
  //   ?saved_id={id}    — load a specific saved snapshot from /asset-vault.
  //                       Skips both the live doc and the localStorage draft.
  //                       Renders the Blueprint report directly (output view).
  useEffect(() => {
    const isNew = searchParams.get("new") === "1";
    const savedId = searchParams.get("saved_id");
    (async () => {
      if (isNew) {
        // Fresh creation — nuke any leftover draft and stay on input view.
        try { localStorage.removeItem(draftKey); } catch {}
        setBlueprint(null);
        setValues({});
        setContentState({ paste: "", urls: [], mode: null, writingSample: "" });
        setView("input");
        // Strip the ?new flag from the URL so a refresh doesn't re-clear.
        setSearchParams({}, { replace: true });
        setHydrated(true);
        return;
      }
      if (savedId) {
        // Load a specific saved snapshot. Skip draft restoration so the
        // snapshot view is clean.
        try {
          const res = await api.get(`/asset-vault/${encodeURIComponent(savedId)}`);
          if (res.data?.blueprint) {
            setBlueprint(res.data.blueprint);
            setUpdatedAt(res.data.updated_at || null);
            setSavedAt(res.data.saved_at || null);
            if (res.data.inputs) {
              setValues(res.data.inputs);
              if (typeof deserializeValues === "function") {
                try {
                  const inferred = deserializeValues(res.data.inputs);
                  if (inferred && typeof inferred === "object") {
                    setContentState((prev) => ({ ...prev, ...inferred }));
                  }
                } catch {}
              }
            }
            setView("output");
          }
        } catch {}
        // Strip the ?saved_id flag — it served its purpose (initial load).
        setSearchParams({}, { replace: true });
        setHydrated(true);
        return;
      }
      try {
        const res = await api.get(loadEndpoint);
        if (res.data?.blueprint) {
          setBlueprint(res.data.blueprint);
          setUpdatedAt(res.data.updated_at);
          setSavedAt(res.data.saved_at || null);
          if (res.data.inputs) {
            setValues(res.data.inputs);
            // Reverse-map backend inputs → contentState (paste/urls/mode/
            // writingSample). Needed because backend `inputs` only stores the
            // serialized payload (user_context, guided fields), not the UI
            // sub-state. Without this reverse mapping, a returning user with
            // cleared localStorage lands on the mode selector and their
            // previously-generated Express input state is invisible.
            // The Wizard-specific `deserializeValues(inputs)` decides mode
            // and re-populates writingSample / urls from the payload.
            if (typeof deserializeValues === "function") {
              try {
                const inferred = deserializeValues(res.data.inputs);
                if (inferred && typeof inferred === "object") {
                  setContentState((prev) => ({ ...prev, ...inferred }));
                }
              } catch {}
            }
          }
          setView("output");
          // Avatar V2: if the loaded blueprint still has supplementary_status
          // === "loading" but the supp keys aren't there yet, that means a
          // previous run was interrupted (server restart, etc). Trigger a
          // retry automatically so the document fills in.
          const bp = res.data.blueprint;
          const isAvatarV2 = agentKey === "client" && bp?.version === "v2";
          const suppMissing = isAvatarV2 && !bp.belso_monolog && !bp.ugyfelnyelv_szotar;
          if (isAvatarV2 && suppMissing && bp.supplementary_status === "loading") {
            try {
              const rr = await api.post("/foundation/avatar/supplementary/retry");
              if (rr.data?.supp_job_id) pollAvatarSupplementary(rr.data.supp_job_id);
            } catch {}
          }
        }
      } catch {}
      // Restore localStorage draft (input answers + mode + content paste/urls
      // + Brand Express writingSample) so the user lands back on the same
      // sub-flow with their work intact.
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft && typeof draft === "object") {
            if (draft.values && typeof draft.values === "object") {
              setValues((prev) => ({ ...prev, ...draft.values }));
            }
            if (draft.contentState && typeof draft.contentState === "object") {
              setContentState((prev) => ({ ...prev, ...draft.contentState }));
            }
          }
        }
      } catch {}
      setHydrated(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced write of the full draft (values + UI state) to localStorage.
  // Skipped on the initial hydration pass.
  useEffect(() => {
    if (!hydrated) return;
    const t = setTimeout(() => {
      try {
        const hasValues = values && Object.keys(values).length > 0;
        const cs = contentState || {};
        const hasContent =
          (cs.paste && cs.paste.trim()) ||
          (Array.isArray(cs.urls) && cs.urls.length > 0) ||
          (cs.writingSample && cs.writingSample.trim()) ||
          cs.mode;
        if (hasValues || hasContent) {
          localStorage.setItem(
            draftKey,
            JSON.stringify({ values, contentState, t: Date.now() })
          );
        } else {
          localStorage.removeItem(draftKey);
        }
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [values, contentState, hydrated, draftKey]);

  const handleGenerate = async (valuesOverride) => {
    const isPlainOverride =
      valuesOverride &&
      typeof valuesOverride === "object" &&
      !valuesOverride.nativeEvent &&
      !valuesOverride._reactName;
    const raw = isPlainOverride ? valuesOverride : values;
    const payload =
      isPlainOverride || typeof serializeValues !== "function"
        ? raw
        : serializeValues(raw, contentState);
    setGenerating(true);
    setGenProgress("Indítás…");
    setGenError(null);
    const myToken = ++genTokenRef.current;
    try {
      const res = await api.post(generateEndpoint, payload, { timeout: 60000 });
      if (myToken !== genTokenRef.current) return;
      let data = res.data;

      // Backend may return either inline ({blueprint,...}) or async ({job_id, status:"running"}).
      // For async, poll until done. Polls last up to 5 minutes.
      if (!data?.blueprint && data?.job_id) {
        const jobId = data.job_id;
        const startedAt = Date.now();
        setGenProgress("Generálás folyik…");
        // First poll right away (no 2s dead-time after kickoff), then 1.2s.
        let firstPoll = true;
        while (Date.now() - startedAt < 5 * 60 * 1000) {
          if (myToken !== genTokenRef.current) return;
          await new Promise((r) => setTimeout(r, firstPoll ? 400 : 1200));
          firstPoll = false;
          const poll = await api.get(`/generation/jobs/${jobId}`, { timeout: 15000 });
          const pdata = poll.data;
          if (pdata.progress) setGenProgress(pdata.progress);
          if (pdata.status === "done") {
            data = pdata.result || {};
            break;
          }
          if (pdata.status === "error") {
            throw new Error(pdata.error || "Generation failed");
          }
        }
        if (!data?.blueprint) {
          throw new Error("Generálás időtúllépés - próbáld újra");
        }
      }

      setBlueprint(data.blueprint);
      setUpdatedAt(data.updated_at);
      setSavedAt(null);
      setView("output");
      toast.success("Blueprint elkészült");

      // Avatar V2: if the backend returned a supp_job_id, kick off the
      // background poll so the 13 supplementary sections merge in as soon
      // as they're ready.
      if (agentKey === "client" && data.supp_job_id) {
        pollAvatarSupplementary(data.supp_job_id);
      }
    } catch (e) {
      if (myToken !== genTokenRef.current) return;
      const msg = e?.response?.data?.detail || e?.message || "Generálás sikertelen";
      toast.error(msg);
      setGenError(msg);
    } finally {
      if (myToken === genTokenRef.current) {
        setGenerating(false);
        setGenProgress("");
      }
    }
  };

  const handlePickNiche = (niche) => {
    if (!niche?.values) return;
    setValues(niche.values);
    setRandomNiche(niche.label || null);
    toast.message(`${niche.label} niche betöltve`, {
      description: "Nyomd meg a Blueprint generálása gombot.",
    });
  };

  const handleRandomTest = async (avatarBlueprint = null, avatarSourceId = null) => {
    if (generating) return;

    // Preferred path: Avatar-aware AI-generated DEMO via backend endpoint.
    // `demoFillEndpoint` is passed by OfferCraft. Voice/Avatar agents don't set
    // it and fall through to the local sampler below.
    if (demoFillEndpoint && avatarSourceId) {
      try {
        const resp = await api.post(demoFillEndpoint, {
          avatar_source_id: avatarSourceId,
        }, { timeout: 45000 });
        const data = resp.data || {};
        if (data.values) {
          setValues(data.values);
          setRandomNiche(data.niche || null);
          toast.message("🎲 DEMO kitöltés kész", {
            description: `${data.niche || "Avatar"} · most nyomd meg a Blueprint generálása gombot`,
          });
          return;
        }
      } catch (e) {
        const msg =
          e?.response?.data?.detail ||
          e?.message ||
          "Nem sikerült a DEMO kitöltés - próbáld újra.";
        toast.error(msg);
        return;
      }
    }

    // Legacy local sampler path (Voice / Avatar / Brand agents).
    if (!pickRandomSample) return;
    const sample = pickRandomSample(avatarBlueprint);
    if (!sample?.values) return;
    setValues(sample.values);
    setRandomNiche(sample.niche || null);
    toast.message("🎲 DEMO niche betöltve", {
      description: `${sample.niche} - most nyomd meg a Blueprint generálása gombot`,
    });
  };

  // INPUT-ONLY reset: csak a Beállítás oldali BEMENETI ÉRTÉKEKET üríti.
  // NE reseteljünk semmilyen UI/nézeti state-et (mode, view, nyitott
  // kártya, scroll). A user tudatos „Törlés" gombkattintása CSAK a
  // gépelt/beadott input értékeket ürítse — a Wizard aktuális Express /
  // Építő módja megmarad, a kártya nyitva marad, a riport érintetlen.
  //
  // TILOS: setView, setMode (null), oldal reload, komponens remount.
  // A kész riportot (blueprint), backend live draft-ot, Tudástár
  // rekordot és Aktív alap pointert EGYÁLTALÁN NEM érinti.
  const handleClear = async () => {
    if (clearing) return;
    setClearing(true);
    try {
      // Guided (Építő) inputok ürítése.
      setValues({});
      // DEMO niche pill címke ürítése (csak akkor van, ha a user DEMO-val
      // töltötte ki — az egy input-alapú label, tehát ürítjük).
      setRandomNiche(null);
      // Express bemenet mezők ÜRÍTÉSE — DE a `mode` MEGMARAD, hogy a
      // Wizard ne váltson vissza a mode selector nézetre. A user
      // ugyanabban a módban marad, üres inputmezőkkel.
      setContentState((prev) => ({
        ...prev,
        paste: "",
        urls: [],
        writingSample: "",
        // mode marad prev.mode
      }));
      // A persistált input draft is ürül; ha van kevés maradék állapot
      // (pl. mode), a következő debounced write újraírja csak azzal.
      try { localStorage.removeItem(draftKey); } catch {}
      toast.success("Beállítások törölve");
    } finally {
      setClearing(false);
    }
  };

  // === Save flow with modal ===
  // First save: simple "Név" prompt, default "Márkahang - YYYY.MM.DD".
  // Subsequent saves (when an entry already exists): "Felülírás" vs "Új mentés".
  const [saveModal, setSaveModal] = useState(null); // null | {existing, defaultName}

  const todayDefault = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${blueprintName || "Márkahang"} - ${y}.${m}.${day}`;
  };

  const openSaveModal = async () => {
    let existing = null;
    try {
      const res = await api.get("/asset-vault");
      const list = (res.data?.items || []).filter((x) => x.agent_type === dashboardKey);
      if (list.length > 0) existing = list[0];
    } catch {}
    setSaveModal({
      existing,
      defaultName: todayDefault(),
    });
  };

  const handleSaveConfirm = async ({ mode, name, existingId }) => {
    // Fire the save chime SYNCHRONOUSLY at the top of the click handler so
    // the browser's autoplay policy treats it as a user-gesture audio event.
    // Chime decays in ~0.8s; we navigate at 900ms — minimal gap to the
    // Műhely cannon while preventing chime/cannon overlap. Erős user-
    // perceptual gyorsítás (volt: 1400ms → új: 900ms ~36% gyorsabb a
    // mentés → konfetti közötti várakozás).
    playSaveChime();
    setSavingVault(true);
    try {
      if (mode === "overwrite" && existingId) {
        await api.delete(`/asset-vault/${existingId}`);
      }
      const res = await api.post(saveEndpoint, { saved: true, name });
      setSavedAt(res.data.saved_at);
      toast.success("Mentve a Tudástárba");
      setSaveModal(null);
      setTimeout(() => navigate(`/dashboard?celebrate=${dashboardKey}`), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen");
    } finally {
      setSavingVault(false);
    }
  };

  const handleExportPdf = async () => {
    if (!blueprint) {
      toast.error("Nincs blueprint");
      return;
    }
    // Foundation Avatar/Ajánlat/Márkahang → szerveroldali Playwright
    // `page.pdf()` a TÉNYLEGES UI riportel. Single source of truth.
    //
    // A felületen NYITOTT V2Collapsible szekciók ID-jét átadjuk az
    // exportnak, hogy a print render pontosan ezeket nyissa ki — a
    // csukottak csukva maradnak.
    const openIds = collectOpenCollapsibleIds();
    setExporting(true);
    try {
      // Async job flow — a szinkron endpoint 60s+ generálás esetén
      // 504 gateway timeoutra futna Production ingress mögött. Ezért
      // job_id + polling: start → poll → download.
      const blob = await runPdfExportJob({
        start: () =>
          api.post("/export/pdf/blueprint/start", {
            blueprint_type: dashboardKey,
            open_sections: openIds.length ? openIds.join(",") : undefined,
          }),
      });
      downloadBlobAsFile(blob, `${blueprintName || dashboardKey}.pdf`);
      toast.success("PDF letöltve");
    } catch (e) {
      console.error("[PDF]", e);
      const detail = e?.response?.data?.detail || e?.message || "Ismeretlen hiba";
      toast.error("PDF generálás sikertelen: " + detail);
    } finally {
      setExporting(false);
    }
  };

  // ── Az appban látható riportban scrape-eljük az NYITOTT V2Collapsible
  //    szekciókat: a button `▾` ikonján `transform: rotate(180deg)` jelzi
  //    a nyitott állapotot. Visszaadunk egy data-testid lista-tömböt
  //    (pl. ["v2-collapsible-belso-monolog", ...]), amit a backend
  //    továbbít a print render-nek `?open=...` paraméterben.
  function collectOpenCollapsibleIds() {
    try {
      const buttons = document.querySelectorAll('button[data-testid^="v2-collapsible-"]');
      const open = [];
      buttons.forEach((b) => {
        const arrow = b.querySelector('span[style*="rotate"]');
        if (!arrow) return;
        const transform = arrow.style.transform || "";
        if (transform.includes("180")) {
          open.push(b.getAttribute("data-testid"));
        }
      });
      return open;
    } catch (e) {
      console.warn("[PDF] collectOpenCollapsibleIds err:", e);
      return [];
    }
  }

  // Rich-HTML copy. Walks the rendered Blueprint DOM, inlines a curated
  // subset of computed styles (NOT padding/margin — embedded <style>
  // controls spacing so editors don't show giant gaps), strips decorative
  // ghosted background numbers, converts 2-col grid containers to <table>
  // (so Snapshot-style key/value rows paste as real tables), and writes
  // both text/html + text/plain to the clipboard via ClipboardItem so
  // users can paste with formatting into Gmail / Google Docs / Word.
  const handleCopy = async () => {
    const el = document.querySelector('[data-testid="blueprint-output"]');
    if (!el) {
      toast.error("Nincs blueprint tartalom");
      return;
    }
    try {
      // Style props to inline (visual identity preserved).
      // Padding/margin/line-height are deliberately omitted — the
      // embedded stylesheet (below) controls vertical rhythm so that
      // pasted output never has giant blank gaps between blocks.
      const PROPS = [
        "color",
        "background-color",
        "font-family",
        "font-size",
        "font-weight",
        "font-style",
        "text-align",
        "text-transform",
        "text-decoration",
        "letter-spacing",
        "border",
      ];

      // Clone subtree
      const clone = el.cloneNode(true);

      // Walk in tandem so we can mutate the clone while reading the live
      // DOM's computed styles. Returns a new clone node (in case caller
      // wants to swap the original out for grid→table conversion etc).
      const walk = (orig, copy) => {
        if (!orig || !copy) return copy;
        if (orig.nodeType !== 1) return copy;

        const cs = window.getComputedStyle(orig);

        // Strip decorative ghosted background numbers (giant transparent
        // glyphs absolutely positioned behind content).
        if (cs.position === "absolute" && parseFloat(cs.fontSize) > 100) {
          copy.parentNode && copy.parentNode.removeChild(copy);
          return null;
        }
        // Strip empty pointer-events:none decoration divs (corner glows).
        if (
          orig.tagName === "DIV" &&
          cs.pointerEvents === "none" &&
          orig.children.length === 0
        ) {
          copy.parentNode && copy.parentNode.removeChild(copy);
          return null;
        }
        // Strip "decorative shapes": small inline elements that exist
        // only as a colored circle/badge (border-radius >= 25%, no
        // children, very small text content). The renderer uses these
        // as timeline circles / M1/M2 markers — visual chrome only.
        const br = cs.borderRadius || "";
        const isCirclyShape = /50%|9999px|999px/.test(br);
        if (
          isCirclyShape &&
          orig.children.length === 0 &&
          (orig.textContent || "").trim().length <= 3
        ) {
          copy.parentNode && copy.parentNode.removeChild(copy);
          return null;
        }

        // Detect 2-3 col grid containers and convert to <table>. Skip
        // wide repeats like the program-arc timeline (6 cols of 1fr) —
        // visual decoration, not data. We require at most 3 columns.
        if (
          cs.display === "grid" &&
          /^(\s*\d+(\.\d+)?(fr|px|%)\s+){1,2}\d+(\.\d+)?(fr|px|%)\s*$/.test(
            cs.gridTemplateColumns,
          )
        ) {
          const cols = cs.gridTemplateColumns.trim().split(/\s+/).length;
          const table = document.createElement("table");
          // Child elements of `copy` represent the cells in row-major
          // order. Group them into rows of `cols`.
          const cells = Array.from(copy.childNodes).filter(
            (n) => n.nodeType === 1,
          );
          for (let i = 0; i < cells.length; i += cols) {
            const tr = document.createElement("tr");
            for (let j = 0; j < cols && i + j < cells.length; j++) {
              const td = document.createElement("td");
              // Migrate the cell child as the td's only child.
              td.appendChild(cells[i + j]);
              tr.appendChild(td);
            }
            table.appendChild(tr);
          }
          // Replace the original copy with the table in its parent.
          if (copy.parentNode) {
            copy.parentNode.replaceChild(table, copy);
          }
          // Walk inside the new td contents now (using the live grid's
          // children for style read).
          const liveCells = Array.from(orig.childNodes).filter(
            (n) => n.nodeType === 1,
          );
          const tds = table.querySelectorAll("td");
          tds.forEach((td, idx) => {
            const live = liveCells[idx];
            const inner = td.firstChild;
            if (live && inner) walk(live, inner);
          });
          return table;
        }

        // Inline a curated subset of computed styles. Skip background
        // colors that would render as dark blocks on a light editor
        // canvas (the renderer uses dark UI backgrounds, the paste
        // target is typically a white/cream document).
        const decl = PROPS.map((p) => {
          const v = cs.getPropertyValue(p);
          if (!v) return "";
          if (p === "background-color") {
            const m = v.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
            if (m) {
              const r = +m[1], g = +m[2], b = +m[3];
              const a = m[4] !== undefined ? parseFloat(m[4]) : 1;
              if (a < 0.02) return "";
              // Filter out dark rgb backgrounds — they were styled for a
              // dark UI, paste them as transparent so cream-canvas docs
              // stay readable.
              if (r + g + b < 180) return "";
            }
          }
          return `${p}:${v}`;
        })
          .filter(Boolean)
          .join(";");
        if (decl) copy.setAttribute("style", decl);
        copy.removeAttribute("class");

        // Recurse over children (snapshot, since walk may mutate).
        const origKids = Array.from(orig.childNodes);
        const copyKids = Array.from(copy.childNodes);
        for (let i = 0; i < origKids.length; i++) {
          if (copyKids[i]) walk(origKids[i], copyKids[i]);
        }
        return copy;
      };
      walk(el, clone);

      // Embedded stylesheet caps margins/padding so pasted output isn't
      // riddled with giant gaps. Word/Docs/Gmail honors !important inline
      // CSS at the document level.
      const embeddedCss = `
        * { margin-top: 0 !important; margin-bottom: 0 !important; }
        p { margin: 4pt 0 !important; }
        h1, h2, h3, h4, h5, h6 { margin: 6pt 0 3pt !important; }
        ul, ol { margin: 4pt 0 !important; padding-left: 22pt !important; }
        li { margin: 1pt 0 !important; }
        table { border-collapse: collapse !important; margin: 4pt 0 8pt !important; width: 100% !important; }
        td, th { padding: 4pt 10pt !important; vertical-align: top !important; border: 1px solid rgba(124,108,242,0.18) !important; }
      `;

      const html =
        `<style>${embeddedCss}</style>` +
        `<div style="font-family: system-ui, -apple-system, sans-serif; color:#0a0a12; line-height:1.45;">` +
        clone.outerHTML +
        `</div>`;
      const plain = (el.innerText || el.textContent || "").trim();

      if (
        typeof window.ClipboardItem !== "undefined" &&
        navigator.clipboard?.write
      ) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plain);
      } else {
        const ta = document.createElement("textarea");
        ta.value = plain;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }

      setCopiedAt(Date.now());
      toast.success("Vágólapra másolva (formázással)");
    } catch (e) {
      console.error("copy failed", e);
      toast.error("Másolás sikertelen");
    }
  };

  const handleRefine = async (instruction, quote) => {
    if (!refineEndpoint) throw new Error("Refine nincs konfigurálva");
    const res = await api.post(refineEndpoint, { instruction, quote: quote || null });
    return { snippet: res.data.snippet || "", blueprint: res.data.blueprint };
  };

  const handleApply = async (newBlueprint) => {
    const applyEndpoint = (refineEndpoint || "").replace(/\/refine$/, "/apply");
    const previous = blueprint;
    const res = await api.post(applyEndpoint, { blueprint: newBlueprint });
    setBlueprint(newBlueprint);
    setUpdatedAt(res.data.updated_at);
    setSavedAt(null);
    return previous;
  };

  const tab2Disabled = !blueprint;
  const activeTab = view === "output" && blueprint ? "tab2" : "tab1";
  const onTabChange = (tab) => {
    if (tab === "tab1") setView("input");
    else if (tab === "tab2" && blueprint) setView("output");
  };

  const isOutput = view === "output" && blueprint;

  // Slide direction - right→left when going to output, left→right when coming back
  const slideTransition = { duration: 0.32, ease: [0.22, 0.9, 0.32, 1] };

  return (
    <div
      className="flex flex-col"
      style={{ height: "100vh", background: "#0a0a12", overflow: "hidden" }}
    >
      <AgentTabHeader
        agentName={agentName || title}
        deliverableLabel={deliverableLabel}
        accent={accent}
        activeTab={activeTab}
        onTabChange={onTabChange}
        tab2Disabled={tab2Disabled}
      />

      <div
        className="relative flex-1 min-h-0"
        style={{ overflow: "hidden" }}
        data-testid="agent-body"
      >
        <AnimatePresence mode="wait" initial={false}>
          {!hydrated ? (
            <motion.div key="hydrating" className="absolute inset-0" />
          ) : isOutput ? (
            <motion.div
              key="output"
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -40, opacity: 0 }}
              transition={slideTransition}
              className="absolute inset-0 flex flex-col"
            >
              <AgentOutputScreen
                accent={accent}
                agentKey={agentKey}
                blueprintName={blueprintName}
                readyTitle={readyTitle}
                dashboardKey={dashboardKey}
                blueprint={blueprint}
                renderBlueprint={
                  (agentKey === "client" || agentKey === "offer") && renderBlueprint
                    ? (bp) => renderBlueprint({
                        ...(bp || {}),
                        _supp_retry: handleSuppRetry,
                        _on_expand_source: handleExpandSource,
                        _retry_failed_sections: handleRetryFailedSections,
                      })
                    : renderBlueprint
                }
                isSaved={!!savedAt}
                onSave={openSaveModal}
                savingVault={savingVault}
                onCopy={handleCopy}
                copied={isCopied}
                onExportPdf={handleExportPdf}
                exporting={exporting}
                onRefine={handleRefine}
                onApply={handleApply}
                onBackToInput={() => setView("input")}
              />
            </motion.div>
          ) : (
            <motion.div
              key="input"
              initial={{ x: -40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={slideTransition}
              className="absolute inset-0 overflow-y-auto"
            >
              <AgentInputScreen
                accent={accent}
                agentKey={agentKey}
                eyebrow={eyebrow}
                title={title}
                subtitle={subtitle}
                greeting={greeting}
                fastLabel={fastLabel}
                fastDesc={fastDesc}
                guidedLabel={guidedLabel}
                guidedDesc={guidedDesc}
                steps={steps}
                values={values}
                setValues={setValues}
                onGenerate={handleGenerate}
                generating={generating}
                genProgress={genProgress}
                genError={genError}
                onClearGenError={() => setGenError(null)}
                pickRandomSample={pickRandomSample}
                randomNiche={randomNiche}
                onRandomTest={handleRandomTest}
                onClear={handleClear}
                clearing={clearing}
                nichePills={nichePills}
                onPickNiche={handlePickNiche}
                fastTrackKeys={fastTrackKeys}
                cycleDemoPill={cycleDemoPill}
                contentInputMode={contentInputMode}
                eyebrowVariant={eyebrowVariant}
                showContentInput={showContentInput}
                fastVariant={fastVariant}
                requiresAvatarSource={requiresAvatarSource}
                modePrompt={modePrompt}
                guidedIntro={guidedIntro}
                onContentChange={setContentState}
                initialMode={contentState.mode}
                initialWritingSample={contentState.writingSample || ""}
                initialPaste={contentState.paste || ""}
                initialAddedUrls={contentState.urls || []}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <SaveToVaultModal
        open={!!saveModal}
        existing={saveModal?.existing}
        defaultName={saveModal?.defaultName}
        saving={savingVault}
        onCancel={() => !savingVault && setSaveModal(null)}
        onConfirm={handleSaveConfirm}
      />
    </div>
  );
}
